import { DialogType, TabAndPanelContents, TabListAlignment, TabsProps, TabStyles, TabsWidget } from '@tableau/widgets';
import React from 'react';

import { ReactModalOptions } from '~commonInfra/dialogs/ReactModalService';
import { Messages } from '~src/codegen/Localize';
import { LinkedTaskDialogStateContextProvider } from '~src/linkedTasks/components/LinkedTaskDialogStateContextProvider';
import { LinkedTasksDialogContent } from '~src/linkedTasks/components/LinkedTasksDialogContent';
import { cssClass } from '~src/style';
import { SpaceContainerStyles } from '~src/style/SpaceContainerStyles';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { FlowBaseInfo } from '~src/ts/models/FlowInfo';
import { RunFlowTaskInfo } from '~src/ts/models/RunFlowTaskInfo';

import { FlowRunTaskDialogContent } from './FlowRunTaskDialogContent';

const rootClass = 'FlowTaskDialog';

const outerSpacing = cssClass(rootClass, 'outerSpacing', {
  paddingTop: 8
});

const dimensionOverrides = {
  tabHeight: SpaceContainerStyles.SpaceTabHeight,
  tabHorizontalPadding: TabStyles.Spacing.LargeSpace,
  tabPanelPadding: '0',
};

const dialogBodyStyle = {
  padding: 0
};

const tabsStyle = cssClass(rootClass, 'tabs', {
  $nest: {
    'div[role=tablist]': {
      paddingLeft: 18,
      $nest: {
        '& > div[aria-selected=true]': {
          fontWeight: 500
        }
      }
    },
    'div[data-tb-test-id=flowTaskDialogId-tabpanel-container]': {
      display: 'flex'
    },
    'div[role=tabpanel]': {
      height: '100%',
      boxSizing: 'border-box',
      width: '100%'
    }
  }
});

export function getFlowTaskDialog(flowInfo: FlowBaseInfo, dismiss: () => void, outputId?: string): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.newTask(),
    dialogContent: <FlowTaskDialog flowInfo={flowInfo} dismiss={dismiss} outputId={outputId}/>,
    showCloseButton: true,
    dialogWidth: FeatureFlags.incrementalRefresh ? 900 : 600,
    dialogHeight: 525,
    shouldRecenterOnUpdate: true,
    shouldNotCloseOnGlassClick: true,
    dialogBodyStyle
  };
}

export function editFlowRunSchedulesDialog(taskInfo: RunFlowTaskInfo, dismiss: () => void): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.editTask(),
    dialogContent: <FlowRunTaskDialogContent taskInfo={taskInfo} dismiss={dismiss}/>,
    showCloseButton: true,
    dialogWidth: FeatureFlags.incrementalRefresh ? 900 : 600,
    shouldRecenterOnUpdate: true,
    shouldNotCloseOnGlassClick: true,
  };
}

export type FlowTaskDialogProps = {
  flowInfo: FlowBaseInfo;
  dismiss: () => void;
  outputId?: string;
}

export function FlowTaskDialog(props: FlowTaskDialogProps): JSX.Element {
  const tabs: TabAndPanelContents[] = [{
    tabContent: { label: Messages.newTask() },
    panelContent: <FlowRunTaskDialogContent flowInfo={props.flowInfo} dismiss={props.dismiss} singleSelectedOutputId={props.outputId}/>,
  }, {
    tabContent: { label: Messages.linkedTasks() },
    panelContent: <LinkedTasksDialogContent {...props}/>,

  }];
  const tabsProps: TabsProps = {
    anchorTabIndex: 0,
    ariaLabel: Messages.tasks(),
    initialActiveIndex: 0,
    tabListAlignment: TabListAlignment.Left,
    tabs: tabs,
    testId: 'flowTaskDialogId',
    dimensionOverrides
  };
  return (
    <LinkedTaskDialogStateContextProvider>
      <div className={outerSpacing}>
        <div className={SpaceContainerStyles.SpaceTabsBottom}/>
        <TabsWidget {...tabsProps} className={tabsStyle}/>
      </div>
    </LinkedTaskDialogStateContextProvider>
  );
}
